import { getCountries, isAuthRedirectInProgress } from '$lib/api';
import { getErrorMessage } from '$lib/utils/errorMessage';

/**
 * Outcome of attempting to load the authorized countries on app startup.
 *
 * - `ok`: countries fetched successfully.
 * - `auth-redirect`: the fetch failed because auth is not established and a
 *   full-page Keycloak redirect is in progress. Callers should keep the boot
 *   loader up rather than show an error, because the browser is navigating away.
 * - `error`: a genuine network/API failure. Callers should show the error UI
 *   with a retry.
 */
export type CountriesResult =
  | { status: 'ok'; countries: string[] }
  | { status: 'auth-redirect' }
  | { status: 'error'; message: string };

interface ResolveCountriesDeps {
  fetchCountries: () => Promise<string[]>;
  authRedirectInProgress: () => boolean;
}

const defaultDeps: ResolveCountriesDeps = {
  fetchCountries: () => getCountries() as unknown as Promise<string[]>,
  authRedirectInProgress: isAuthRedirectInProgress
};

/**
 * Attempts to load the authorized countries, classifying the outcome so the
 * load layer never throws for non-auth failures (which would leave the SPA
 * boot loader lingering with no way to mount the error UI).
 *
 * Dependencies are injectable for testing.
 */
export async function resolveCountries(
  deps: ResolveCountriesDeps = defaultDeps
): Promise<CountriesResult> {
  try {
    const countries = await deps.fetchCountries();
    return { status: 'ok', countries };
  } catch (e) {
    // An auth failure already triggered a Keycloak redirect; don't surface an
    // error screen over the top of that navigation.
    if (deps.authRedirectInProgress()) {
      return { status: 'auth-redirect' };
    }
    // Log the raw error so boot-time failures stay visible in the console /
    // monitoring; the formatted message is only for the UI.
    console.error('Failed to load authorized countries on startup:', e);
    return { status: 'error', message: getErrorMessage(e) };
  }
}
